import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import {
  GenericMenuItemInfo
} from '@tableau/widgets';

import { Messages } from '../../../codegen/Localize';
import { selectFlowsOnSiteEnabled } from '../../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { MixedContentInOrder } from '../../../ts/models/ContentDefinitions';
import { contentCountsRequest } from '../../actions/spaces/ContentCountsActions';
import { selectContentCounts, selectContentCountsUpToDate } from '../../selectors/ContentCountsSelector';
import { selectSiteSpaceContentType } from '../../selectors/ContentFilterSelectors';
import { SessionSiteSelectors } from '../../selectors/SessionInfoSelectors';
import { AppState } from '../../state/AppState';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { SITE_SPACE_EXPLORE, SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { ExplorerDropdownItemFormatter } from '../Components/ExplorerDropdownPanelItems';

import {
  ExplorerListMenuPanel,
  ExplorerListMenuPanelDispatchProps,
  ExplorerListMenuPanelOwnProps,
  ExplorerListMenuPanelStateProps
} from '../Components/ExplorerListMenuPanel';

function isChecked(contentType: SiteSpaceContentType, currentContentType: SiteSpaceContentType): boolean {
  return currentContentType === contentType ||
    (contentType === 'datasource' && currentContentType === 'dataconnection');
}

function getMenuItems(
  currentContentType: SiteSpaceContentType,
  contentCounts: ISpaceInfoTypeItemCount<'site'>,
  contentCountsUpToDate: boolean,
  flowsEnabled: boolean,
): GenericMenuItemInfo[] {

  let menuItems: GenericMenuItemInfo[] = [];
  menuItems.push({
    value: SITE_SPACE_EXPLORE,
    disabled: false,
    checked: isChecked(SITE_SPACE_EXPLORE, currentContentType),
    displayFormatterBoundWithData: ExplorerDropdownItemFormatter(
      Messages.siteFilterByTypeDropdown_TopLevelProjects(),
      contentCountsUpToDate && contentCounts && contentCounts['explore'],
      contentCountsUpToDate),
    keyboardAccessibleValue: Messages.siteFilterByTypeDropdown_TopLevelProjects()
  });

  menuItems = menuItems.concat(_.compact(MixedContentInOrder.map((contentDefinition) => {
    if (
      (contentDefinition.itemType !== 'flow' || flowsEnabled) &&
      (contentDefinition.itemType !== 'lens' || FeatureFlags.nlplensContentTypeEnabled) &&
      (contentDefinition.itemType !== 'metric' || FeatureFlags.metricsSpaceEnabled) &&
      (contentDefinition.itemType !== 'publishedConnection' || FeatureFlags.publishedConnectionContentManagementEnabled)) {
       return {
        value: contentDefinition.itemType,
        disabled: false,
        checked: isChecked(contentDefinition.itemType, currentContentType),
        displayFormatterBoundWithData: ExplorerDropdownItemFormatter(
          contentDefinition.siteFilterByTypeDropdownName(),
          contentCountsUpToDate && contentCounts && contentCounts[contentDefinition.itemType],
          contentCountsUpToDate),
        keyboardAccessibleValue: contentDefinition.siteFilterByTypeDropdownName()
      } as GenericMenuItemInfo;
    }
  })));
  return menuItems;
}

const getMenuItemsWrapper = defaultMemoize(getMenuItems);

const mapStateToProps: MapStateToProps<ExplorerListMenuPanelStateProps> =
  (state: AppState) => {
    const currentContentType = selectSiteSpaceContentType(state);
    const contentCounts = selectContentCounts<'site'>(state, 'site');
    const contentCountsUpToDate = selectContentCountsUpToDate(state);
    const flowsEnabled = selectFlowsOnSiteEnabled(state);
    const menuItems = getMenuItemsWrapper(currentContentType, contentCounts, contentCountsUpToDate, flowsEnabled);
    return {
      menuItems
    };
  };

const mapDispatchToProjectProps: MapDispatchToProps<ExplorerListMenuPanelDispatchProps> =
  (dispatch) => bindActionCreators({
    onComponentDidUpdate: contentCountsRequest
  },
  dispatch);

export const ConnectedSiteLevelFilterMenu: React.ComponentClass<ExplorerListMenuPanelOwnProps> =
  connect(mapStateToProps, mapDispatchToProjectProps)(ExplorerListMenuPanel);
